import { useEffect, useState, useCallback } from "react";
import { reviewsAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Star, BadgeCheck, Flag, Pencil, Trash2, Loader2 } from "lucide-react";

function Stars({ value, onChange, size = 16 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onChange}
          onClick={() => onChange?.(n)}
          data-testid={onChange ? `star-${n}` : undefined}
          className={onChange ? "cursor-pointer" : "cursor-default"}
        >
          <Star style={{ width: size, height: size }} className={n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"} />
        </button>
      ))}
    </div>
  );
}

export default function ReviewsSection({ sellerId, kind }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    reviewsAPI.getForSeller(sellerId, kind)
      .then((r) => setData(r.data))
      .catch(() => setData({ reviews: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [sellerId, kind]);

  useEffect(() => { if (sellerId) load(); }, [sellerId, load]);

  const isSeller = user?.id === sellerId;
  const myReview = data.reviews.find((r) => r.buyer_id === user?.id);
  const ratingVal = kind === "service" ? data.service_rating : data.product_rating;
  const ratingCount = kind === "service" ? data.service_reviews : data.product_reviews;

  const submit = async () => {
    if (!rating) return toast.error("Pick a star rating");
    setBusy(true);
    try {
      if (editingId) {
        await reviewsAPI.update(editingId, { rating, review_text: text });
        toast.success("Review updated");
      } else {
        await reviewsAPI.create({ seller_id: sellerId, review_kind: kind, rating, review_text: text });
        toast.success("Review posted");
      }
      setRating(0); setText(""); setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit review");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (r) => { setEditingId(r.id); setRating(r.rating); setText(r.review_text || ""); };
  const remove = async (r) => {
    if (!window.confirm("Remove your review?")) return;
    try { await reviewsAPI.remove(r.id); toast.success("Removed"); load(); } catch { toast.error("Could not remove"); }
  };
  const report = async (r) => {
    const reason = window.prompt("Why are you reporting this review? (unfair / didn't buy / abuse)");
    if (reason === null) return;
    try { await reviewsAPI.report(r.id, reason); toast.success("Reported to admins"); load(); } catch { toast.error("Could not report"); }
  };

  return (
    <div className="space-y-4" data-testid="reviews-section">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">Reviews</h2>
        {ratingCount > 0 && (
          <span className="flex items-center gap-1.5 text-sm">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <span className="font-semibold">{Number(ratingVal).toFixed(1)}</span>
            <span className="text-muted-foreground">({ratingCount})</span>
          </span>
        )}
      </div>

      {user && !isSeller && (
        <div className="rounded-lg border border-border bg-card p-4" data-testid="write-review">
          <p className="mb-2 text-sm font-medium">{myReview && !editingId ? "Your review" : editingId ? "Edit your review" : "Write a review"}</p>
          {myReview && !editingId ? (
            <div className="flex items-center gap-3">
              <Stars value={myReview.rating} />
              <Button size="sm" variant="outline" onClick={() => startEdit(myReview)} data-testid="edit-my-review"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
              <Button size="sm" variant="ghost" onClick={() => remove(myReview)} data-testid="remove-my-review"><Trash2 className="mr-1 h-3.5 w-3.5" /> Remove</Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Stars value={rating} onChange={setRating} size={22} />
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Share your experience..." data-testid="review-text-input" />
              <div className="flex gap-2">
                <Button size="sm" onClick={submit} disabled={busy} className="bg-primary text-primary-foreground" data-testid="submit-review-button">
                  {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />} {editingId ? "Save" : "Post review"}
                </Button>
                {editingId && <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setRating(0); setText(""); }}>Cancel</Button>}
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading reviews...</p>
      ) : data.reviews.length === 0 ? (
        <p className="text-sm text-muted-foreground">No reviews yet.</p>
      ) : (
        <div className="space-y-3">
          {data.reviews.map((r) => (
            <div key={r.id} className="rounded-lg border border-border bg-card p-4" data-testid={`review-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8"><AvatarImage src={r.buyer_avatar} /><AvatarFallback>{r.buyer_username?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
                  <div>
                    <p className="flex items-center gap-1.5 text-sm font-semibold">
                      {r.buyer_username}
                      {r.verified && <span className="flex items-center gap-0.5 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-500" data-testid="verified-badge"><BadgeCheck className="h-3 w-3" /> Verified customer</span>}
                    </p>
                    <Stars value={r.rating} size={13} />
                  </div>
                </div>
                {isSeller && !r.under_review && (
                  <Button size="sm" variant="ghost" onClick={() => report(r)} data-testid={`report-review-${r.id}`}><Flag className="mr-1 h-3.5 w-3.5" /> Report</Button>
                )}
              </div>
              {r.under_review ? (
                <p className="mt-2 text-xs italic text-amber-500" data-testid="review-under-review">Reported — under review by our team.</p>
              ) : r.review_text ? (
                <p className="mt-2 text-sm text-foreground/80">{r.review_text}</p>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
